import * as React from 'react'
import { FC, useCallback, useState } from 'react'
import { getStyles, useNotificationDataOnMount } from './hooks'
import { INotification } from '@user/store/notifications/typings'

import { SettingsPage, SideDrawer } from '@sales-center/components'
import {
  AutoReminderNotification,
  InternalNotification,
  NotificationCard,
  PayOverTimeCompletedNotification,
} from './atoms'
import { ManageSignedAgreementSlideout, ManageSlideout } from './forms'

import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import { useTheme } from '@mui/material/styles'

export const Notifications: FC = () => {
  const styles = getStyles(useTheme())

  const { loading, customerSigned, internal, autoReminder, payOverTimeCompleted } =
    useNotificationDataOnMount()

  const [custSignedOpen, setCustSignedOpen] = useState(false)
  const onOpenCustSigned = useCallback(() => setCustSignedOpen(true), [])
  const onCloseCustSigned = useCallback(() => setCustSignedOpen(false), [])

  const [toEdit, setToEdit] = useState<INotification>()
  const clearEditId = useCallback(() => setToEdit(undefined), [])

  return (
    <SettingsPage title="Notifications">
      {loading ? (
        <Box margin="auto">
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <>
          <Typography sx={{ ...styles.header, ...styles.firstHeader }}>
            Customer Notifications
          </Typography>

          <NotificationCard
            type="Customer"
            name="Agreement Sent to Customer"
            helperText="The customer receives this notification when an agreement is sent to them to complete"
          />

          {!!customerSigned && (
            <>
              <NotificationCard
                type="Customer"
                name={customerSigned.notificationEventName}
                helperText={customerSigned.helperText}
                rightContent={
                  <Button
                    color="primary"
                    variant="outlined"
                    onClick={onOpenCustSigned}
                    data-test-id="manageNotificationBtn"
                  >
                    Manage
                  </Button>
                }
              />
              <SideDrawer
                title="Manage Notifications"
                open={custSignedOpen}
                onClose={onCloseCustSigned}
              >
                <ManageSignedAgreementSlideout
                  notification={customerSigned}
                  onClose={onCloseCustSigned}
                  type="Customer"
                />
              </SideDrawer>
            </>
          )}

          {!!autoReminder && (
            <>
              <AutoReminderNotification
                key={autoReminder.id}
                notification={autoReminder}
              />
            </>
          )}

          {!!payOverTimeCompleted && (
            <>
              <PayOverTimeCompletedNotification
                key={payOverTimeCompleted.id}
                notification={payOverTimeCompleted}
              />
            </>
          )}

          <Typography sx={{ ...styles.header }}>
            Internal Notifications
          </Typography>

          {internal.map((n) => (
            <InternalNotification
              key={n.id}
              notification={n}
              setToEdit={setToEdit}
            />
          ))}

          <SideDrawer
            title="Manage Notifications"
            open={!!toEdit}
            onClose={clearEditId}
            tidalThemeActive
            titleVariant="h5"
          >
            {!!toEdit && (
              <ManageSlideout notification={toEdit} onClose={clearEditId} />
            )}
          </SideDrawer>
        </>
      )}
    </SettingsPage>
  )
}
Notifications.displayName = 'Notifications'
